import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Text,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { useQueryClient } from "react-query";
import CategoryForm from "./CategoryForm";
import {
  organizationQuestionnaireKeys,
  purchasedGoodsAndServicesKeys,
  useAddPurchasedProductItems,
  useDeletePurchasedProductItem,
  useDownloadPurchasedGoodsAndServicesTemplate,
  useGetLocations,
  useGetPurchasedProductItems,
  useImportPurchasedProductItems,
  useUpdatePurchasedProductItem
} from "../../../../../../hooks/query";
import { showErrorToast, showSuccessToast } from "../../../../../../utils";
import * as React from "react";
import { useCallback, useState } from "react";
import { CategoryGrid } from "../CategoryGrid";
import { DeleteDialog } from "../../../../../DataEntryPlanning/components/DeleteDialog";
import { hideAppSpinner, showAppSpinner } from "../../../../../../context/Store/slices/appSpinnerSlice";
import { useDispatch } from "react-redux";
import { PurchasedAndCapitalGoodsModalWrapper } from "../CategoryModalWrappers";
import { withDimensionItems } from "./helpers";
import WaterUsageGrid from "./WaterUsageGrid";
import { targetForCategory } from "../../../../aiIngestTarget";

const FORM_INPUT_KEY = "purchasedGoods";

function PurchasedGoodsAndServices({
                                     defaultValues,
                                     tabKey,
                                     handleCategoryChange,
                                     finalize,
                                     finalizeQuestionnaire,
                                     isPreview,
                                     organizationStructure,
                                     dimensionItems
                                   }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { t: globalTranslation } = useTranslation("translation");
  const { t } = useTranslation("translation", {
    keyPrefix: "organizationQuestionnaire.scope3"
  });
  const dispatch = useDispatch();

  const {
    data: locations
  } = useGetLocations({
    onError: err => handleErrors(err)
  });

  const {
    id: questionnaireId
  } = defaultValues;

  const colHeaders = [
    t(`inputs.${tabKey}.supplierName`),
    t(`inputs.${tabKey}.location`),
    t(`inputs.${tabKey}.productCode`),
    t(`inputs.${tabKey}.itemDescription`),
    "Amount spent"
  ];
  const attributesOrder = ["supplierName", "location", "productCategory", "itemDescription", "spentMoneyAmount"];

  const [categoryModalData, setCategoryModalData] = useState(null);

  const {
    isOpen: isCategoryModalOpen,
    onOpen: onCategoryModalOpen,
    onClose: onCategoryModalClose
  } = useDisclosure();

  const {
    isOpen: isDeleteItemDialogOpen,
    onOpen: onDeleteItemDialogOpen,
    onClose: onDeleteItemDialogClose
  } = useDisclosure();

  const handleErrors = (err) => {
    if (err.response?.status === 404) {
      return;
    }
    showErrorToast(toast, err.message);
  };

  const { handleSubmit } = useForm();

  const prepareSectionValues = useCallback(items => {
    return items.map(item => {
      return {
        id: item.id ?? null,
        dimensionItems: item.dimensionItems ?? null,
        dimensionOneItem: item.dimensionItems?.dimensionOneItem ?? null,
        dimensionTwoItem: item.dimensionItems?.dimensionTwoItem ?? null,
        dimensionThreeItem: item.dimensionItems?.dimensionThreeItem ?? null,
        dimensionFourItem: item.dimensionItems?.dimensionFourItem ?? null,
        dimensionFiveItem: item.dimensionItems?.dimensionFiveItem ?? null,
        location: locations?.find(el => el.id === item.locationId)?.location ?? "",
        locationId: item.locationId,
        productCategory: `${item.productCode} - ${item.productCategory}`,
        productCategoryId: item.productCategoryId ?? null,
        supplierName: item.supplierName,
        itemDescription: item.itemDescription,
        spentMoneyAmount: item.spentMoneyAmount
      };
    });
  }, [locations]);

  const handleAddItem = () => {
    setCategoryModalData({
      fieldArrayName: FORM_INPUT_KEY,
      columnHeaders: [...colHeaders, ""],
      modalHeaderItemsType: t(`inputs.purchasedGoodsAndServices.itemsType`),
      organizationStructure,
      dimensionItems
    });
    onCategoryModalOpen();
  };

  const handleEditItem = (item) => {
    setCategoryModalData({
      fieldArrayName: FORM_INPUT_KEY,
      editData: {
        id: item.id,
        dimensionItems: item.dimensionItems ?? null,
        productCategory: item.productCategory,
        productCategoryId: item.productCategoryId,
        supplierName: item.supplierName,
        itemDescription: item.itemDescription,
        spentMoneyAmount: item.spentMoneyAmount,
        locationId: item.locationId,
        location: item.location
      },
      columnHeaders: colHeaders,
      modalHeaderItemsType: t(`inputs.purchasedGoodsAndServices.itemsType`),
      organizationStructure,
      dimensionItems
    });
    onCategoryModalOpen();
  };

  const [selectedItem, setSelectedItem] = useState(null);

  const handleDeleteItem = (item) => {
    onDeleteItemDialogOpen();
    setSelectedItem({ id: item.id });
  };

  const handleDeleteItemDialogClose = () => {
    setSelectedItem(null);
    onDeleteItemDialogClose();
  };

  const deleteRecord = () => {
    dispatch(showAppSpinner());
    deleteItem({ questionnaireId, recordId: selectedItem.id });
  };

  const formId = `formStep${tabKey}`;

  const { mutate: deleteItem } = useDeletePurchasedProductItem({
    onSuccess: () => {
      handleDeleteItemDialogClose();
      showSuccessToast(toast, globalTranslation("organizationQuestionnaire.page.deleted"));
      queryClient.invalidateQueries(purchasedGoodsAndServicesKeys.items());
    },
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => dispatch(hideAppSpinner())
  });

  const { mutate: downloadTemplate } = useDownloadPurchasedGoodsAndServicesTemplate({
    onMutate: () => dispatch(showAppSpinner()),
    onError: err => {
      const parsed = err.response?.data
        ? JSON.parse(String.fromCharCode.apply(null, new Int8Array(err.response.data)))
        : null;
      showErrorToast(toast, parsed?.message ?? err.message);
    },
    onSettled: () => dispatch(hideAppSpinner())
  });

  const { mutate: importItems } = useImportPurchasedProductItems({
    onSuccess: () => {
      showSuccessToast(toast, globalTranslation("organizationQuestionnaire.page.imported"));
      queryClient.invalidateQueries(purchasedGoodsAndServicesKeys.items());
    },
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => dispatch(hideAppSpinner())
  });

  const handleImport = (e) => {
    if (!e.target.files?.[0]) return;
    const formData = new FormData();
    formData.append("file", e.target.files[0]);
    dispatch(showAppSpinner());
    importItems({ questionnaireId, data: formData });
  };

  const onSubmit = (() => {
    if (!isPreview && finalize) {
      finalizeQuestionnaire(questionnaireId);
    } else {
      // Water-usage rows persist via their own endpoints; product items via theirs.
      // Nothing else at the category level needs saving on Next/Finalize.
      queryClient.invalidateQueries(organizationQuestionnaireKeys.orgQuestionnaireById());
      handleCategoryChange();
    }
  });

  const handleCategoryModalClose = () => {
    setCategoryModalData(null);
    onCategoryModalClose();
  };

  const { mutate: addItem } = useAddPurchasedProductItems({
    onSuccess: () => {
      handleCategoryModalClose();
      showSuccessToast(toast, globalTranslation("organizationQuestionnaire.page.added"));
      queryClient.invalidateQueries(purchasedGoodsAndServicesKeys.items());
    },
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => dispatch(hideAppSpinner())
  });

  const { mutate: updateItem } = useUpdatePurchasedProductItem({
    onSuccess: () => {
      handleCategoryModalClose();
      showSuccessToast(toast, globalTranslation("organizationQuestionnaire.page.updated"));
      queryClient.invalidateQueries(purchasedGoodsAndServicesKeys.items());
    },
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => dispatch(hideAppSpinner())
  });

  const handleCategoryModalSubmit = (formData) => {
    const apiData = formData[categoryModalData.fieldArrayName].map(el => withDimensionItems({ ...el }));

    dispatch(showAppSpinner());
    if (categoryModalData.editData) {
      updateItem({
        questionnaireId,
        recordId: apiData[0].id,
        data: apiData[0]
      });
    } else {
      addItem({
        questionnaireId,
        data: apiData
      });
    }
  };

  return (
    <CategoryForm onSubmit={handleSubmit(onSubmit)} id={formId} marginTop={4}>
      <Box overflow="auto">
        <Accordion defaultIndex={[1]} allowMultiple marginBottom="10">
          {/* Water Usage */}
          <AccordionItem borderBottom="none">
            <AccordionButton px={0} pt={2} pb={4} _hover={{ bg: "transparent" }}>
              <Text as="h2" fontSize="heading.m" fontWeight="700" color="gray.600">
                {t(`inputs.waterUsage`)}
              </Text>
              <AccordionIcon mr={0} ml="auto" boxSize={6} />
            </AccordionButton>
            <AccordionPanel px={0} overflow="auto">
              <WaterUsageGrid
                questionnaireId={questionnaireId}
                organizationStructure={organizationStructure}
                dimensionItems={dimensionItems}
                isPreview={isPreview}
                tabKey={tabKey}
              />
            </AccordionPanel>
          </AccordionItem>

          {/* Purchased Goods & Services items */}
          <AccordionItem borderBottom="none">
            <AccordionButton px={0} pt={2} pb={4} _hover={{ bg: "transparent" }}>
              <Text as="h2" fontSize="heading.m" fontWeight="700" color="gray.600">
                Purchased Goods &amp; Services
              </Text>
              <AccordionIcon mr={0} ml="auto" boxSize={6} />
            </AccordionButton>
            <AccordionPanel px={0} overflow="auto">
              {locations && (
                <CategoryGrid
                  questionnaireId={questionnaireId}
                  columnHeaders={colHeaders}
                  attributesOrder={attributesOrder}
                  prepareSectionValues={prepareSectionValues}
                  handleAddItem={handleAddItem}
                  handleEditItem={handleEditItem}
                  handleDeleteItem={handleDeleteItem}
                  getItemsFunction={useGetPurchasedProductItems}
                  isPreview={isPreview}
                  maxHeight={isPreview ? "240" : "210"}
                  organizationStructure={organizationStructure}
                  onDownloadTemplate={() => downloadTemplate({ questionnaireId })}
                  onImport={handleImport}
                  importBtnId={`import-${tabKey}-btn`}
                  aiTarget={targetForCategory(tabKey)}
                  totalsConfig={{ valueKey: "spentMoneyAmount" }}
                />
              )}
            </AccordionPanel>
          </AccordionItem>
        </Accordion>
      </Box>

      <DeleteDialog
        isOpen={isDeleteItemDialogOpen}
        onClose={handleDeleteItemDialogClose}
        onDelete={deleteRecord}
        entityName={"Item"}
      />
      {
        isCategoryModalOpen && categoryModalData !== null && (
          <PurchasedAndCapitalGoodsModalWrapper
            tabKey={tabKey}
            modalData={categoryModalData}
            attributesOrder={attributesOrder}
            isOpen={isCategoryModalOpen}
            onClose={handleCategoryModalClose}
            onSubmit={handleCategoryModalSubmit}
            locations={locations}
          />
        )
      }
    </CategoryForm>
  );
}

export default PurchasedGoodsAndServices;
